import { MoreVert as MoreIcon } from "@mui/icons-material";
import { Box, Grid, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useCallback, useContext, useEffect, useState } from "react";
import { FieldValues, FormProvider, useForm, Validate } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import AddButton from "../../../common/components/functional/addButton/AddButton";
import BasicDialog from "../../../common/components/functional/dialogs/BasicDialog";
import FormTable from "../../../common/components/functional/formTable/FormTable";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { ActionsIconButton } from "../../../common/components/styled/styled";
import isEmpty from "../../../common/helpers/isEmpty";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { sortCustomerUsers } from "../../../common/utils/users";
import { UnsavedChangesProvider } from "../../../contexts/UnsavedChangesContext";
import useBackendPolling from "../../../hooks/useBackendPolling";
import useMessage from "../../../hooks/useMessage";
import { AccessRightContext } from "../../user/AccessRightProvider";
import { workLocationsByCustomerCompanyId } from "../customerAPI";
import { FormContainer } from "../customerManagement/styles";
import { selectCustomerOrganizationById } from "../customerOrganizationsSlice";
import {
  createNewCustomerUser,
  fetchCustomerUsersByCompanyId,
  fetchCustomerUsersByIds,
  inviteExistingCustomerAppUser,
  selectAllCustomerUsers,
  selectCustomerUsersByCompanyId,
  selectCustomerUsersByIds,
  setCustomerUserTemporaryPassword,
  updateCustomerAppUser,
  updateCustomerAppUserEnabledState,
  updateCustomerUserWorkLocations,
} from "../customerUsersSlice";
import {
  fetchWorkLocationsAndContractsByOrgId,
  selectAllCustomerWorkLocations,
  setWorkLocationsOrderingUsers,
} from "../customerWorkLocationsSlice";
import {
  CreateCustomerUser,
  InviteExistingCustomerUser,
  UpdateCustomerUser,
  UpdateCustomerUserEnabledState,
} from "../types/commands";
import {
  CustomerUser,
  CustomerUserInviteStatus,
  Identifier,
  WorkLocation,
} from "../types/queries";
import { ModalState } from "../types/types";
import UserActionsMenu from "./UserActionsMenu";
import UserChip, { UserChipType } from "./UserChip";
import UserForm from "./UserForm";
import UsersEmptyState from "./UsersEmptyState";
import { MenuItemKey, MenuState } from "./types";
import { TempPasswordDialog } from "../../../common/components/functional/tempPasswordDialog/TempPasswordDialog";

const getIds = (array: AutocompleteType[]) => {
  if (!array.length) return [];
  return array.map((item) => item.id);
};

const compareWorkLocations = (
  updated: AutocompleteType[],
  original: AutocompleteType[],
) => {
  const updatedIds = getIds(updated);
  const originalIds = getIds(original);

  const addTo = updatedIds.filter((item) => !originalIds.includes(item));
  const removeFrom = originalIds.filter((item) => !updatedIds.includes(item));

  return { addTo, removeFrom };
};

const UsersTable = () => {
  const [searchParams] = useSearchParams();
  const [menuState, setMenuState] = useState<MenuState>({
    anchorEl: null,
    userId: "",
  });
  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    id: "",
  });
  const [userIdToPoll, setUserIdToPoll] = useState("");
  const [workLocations, setWorkLocations] = useState<WorkLocation[]>([]);

  const { customerCompanyId } = useParams();
  const customerOrgId = searchParams.get("org");
  const { canEditCustomer } = useContext(AccessRightContext);

  const selectedOrganization = useAppSelector((state) =>
    selectCustomerOrganizationById(state, customerOrgId as string),
  );
  const isRepresentativeOrganization = selectedOrganization?.isRepresentative;

  const [selectedUser, setSelectedUser] = useState<CustomerUser | undefined>(
    undefined,
  );

  useEffect(() => {
    if (customerCompanyId) {
      // all the user's workplaces to be displayed regardless of the selected organization
      // options are fetched from all customer organizations (company level)
      // TODO: add thunk
      workLocationsByCustomerCompanyId({ customerCompanyId }).then((res) => {
        const activeWorkLocations = res.filter(
          (workLocation) => workLocation.active,
        );
        setWorkLocations(activeWorkLocations);
      });
    }
  }, [customerCompanyId]);

  const { sendMessage } = useMessage();

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    startPolling,
    stopPolling,
    isTimeout,
    error,
    isPolling,
    clearErrors,
  } = useBackendPolling({
    callback: async () => poller(),
    errorMessage: t("common:actionTimedOut"),
  });

  useEffect(() => {
    if (isTimeout || error) {
      clearErrors();
      onClose(false);
    }
    return () => stopPolling();
    // eslint-disable-next-line
  }, [isTimeout]);

  const poller = async () => {
    if (customerCompanyId) {
      const response = await dispatch(
        fetchCustomerUsersByCompanyId({ customerCompanyId }),
      ).unwrap();

      const invited = response.find(
        (customerUser) => customerUser.id === userIdToPoll,
      );

      if (invited && userIdToPoll) {
        // proceed after succesful polling
        const customerUserWorkLocations: AutocompleteType[] = methods.getValues(
          "customerUserWorkLocationIds",
        );

        const autocompleteType =
          methods.formState.defaultValues?.customerUserWorkLocationIds || [];
        const defaultCustomerUserWorkLocations =
          autocompleteType as unknown as AutocompleteType[];

        const { addTo, removeFrom } = compareWorkLocations(
          customerUserWorkLocations,
          defaultCustomerUserWorkLocations,
        );

        const params = {
          customerUserId: userIdToPoll,
          addTo,
          removeFrom,
        };

        dispatch(updateCustomerUserWorkLocations(params)).then((action) => {
          if (isFulfilled(action)) {
            dispatch(
              setWorkLocationsOrderingUsers({
                customerUserId: params.customerUserId,
                addTo,
                removeFrom,
                events: action.payload,
              }),
            );

            sendMessage(t("customers:usersNS:successfulCreation"), "success");
            onClose(false);
            stopPolling();
            setUserIdToPoll("");
          }
        });
      }
    }
  };

  const defaultValues = {
    id: "",
    firstName: "",
    lastName: "",
    title: "",
    phone: "",
    emailAddress: "",
    customerUserWorkLocationIds: [],
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const { status: customerUserStatus } = useAppSelector(
    (state) => state.customerUsers,
  );
  const { status: workLocationStatus } = useAppSelector(
    (state) => state.customerWorkLocations,
  );

  const isLoading =
    customerUserStatus === "pending" || workLocationStatus === "pending";

  const onClose = (cancel: boolean) => {
    setModalState({ ...modalState, show: false });

    if (cancel) {
      methods.reset();
    }
  };

  useEffect(() => {
    if (customerCompanyId) {
      dispatch(
        fetchCustomerUsersByCompanyId({
          customerCompanyId,
        }),
      );
    }
  }, [dispatch, customerCompanyId]);

  const allCustomerUsers = useAppSelector(selectAllCustomerUsers);

  const custUserIdsWhoCanOrder = useAppSelector(
    selectAllCustomerWorkLocations,
  ).flatMap((workLocation) => workLocation.custUsersWhoCanOrder);

  const uniqueIds = [...new Set(custUserIdsWhoCanOrder)];

  const uniqueIdentifiers = uniqueIds.map((id) => ({
    id,
  })) as Identifier[];

  const custUsersWhoCanOrder = useAppSelector(
    selectCustomerUsersByIds(uniqueIdentifiers),
  );

  // users that are displayed in the table
  // show all customer users (with work locations) if the topmost organization in the menu (representative organization) is selected
  // otherwise show only users who can order to the selected organization
  const usersToShow = isRepresentativeOrganization
    ? allCustomerUsers.filter(
        (user) =>
          user.account?.customerUserWorkLocationIds &&
          !isEmpty(user.account.customerUserWorkLocationIds),
      )
    : custUsersWhoCanOrder;

  const customerUsersByCompanyId = useAppSelector(
    selectCustomerUsersByCompanyId(customerCompanyId as string),
  );

  // users in the prefill list
  // - empty work locations = user invited, but not showing in the table (error during updating work locations) -> allow to add work locations
  // - migrated users with empty work locations
  const prefilledCustomerUsers = customerUsersByCompanyId.filter(
    (customerUser) => isEmpty(customerUser.account.customerUserWorkLocationIds),
  );

  useEffect(() => {
    if (customerOrgId) {
      dispatch(fetchWorkLocationsAndContractsByOrgId({ id: customerOrgId }));
    }
  }, [dispatch, customerOrgId]);

  const renderWorkLocations = useCallback(
    ({
      isUserEnabled,
      inviteStatus,
      workLocationIds,
    }: {
      isUserEnabled: boolean;
      inviteStatus: CustomerUserInviteStatus;
      workLocationIds?: string[];
    }) => {
      if (!workLocationIds) return [];

      const userWorkLocations = workLocations
        .filter((workLocation) => workLocationIds.includes(workLocation.id))
        .sort((a, b) => a.name.localeCompare(b.name));

      const getChipType = () => {
        if (!isUserEnabled) {
          return UserChipType.Disabled;
        }
        if (inviteStatus !== CustomerUserInviteStatus.INVITE_ACCEPTED) {
          return UserChipType.Secondary;
        }
        return UserChipType.Primary;
      };

      return userWorkLocations.map((workLocation) => (
        <UserChip
          key={workLocation.id}
          id={workLocation.id}
          label={workLocation.name}
          type={getChipType()}
        />
      ));
    },
    [workLocations],
  );

  const renderInviteStatus = ({
    isUserEnabled,
    inviteStatus,
  }: {
    isUserEnabled: boolean;
    inviteStatus?: CustomerUserInviteStatus;
  }) => {
    const deactived =
      !isUserEnabled && inviteStatus !== CustomerUserInviteStatus.UNINVITED;

    return (
      <Typography
        component="span"
        fontSize="inherit"
        {...(isUserEnabled &&
          inviteStatus === CustomerUserInviteStatus.INVITE_ACCEPTED && {
            color: "success",
          })}
      >
        {t(
          `customers:usersNS:${!deactived ? inviteStatus || "UNINVITED" : "deactivated"}`, // fallback to UNINVITED
        )}
      </Typography>
    );
  };

  const formattedCustomerUsers = usersToShow
    .sort(sortCustomerUsers)
    .map(({ id, account }) => ({
      values: {
        name: `${account.firstName} ${account.lastName}`,
        title: account.title,
        phone: account.phone,
        emailAddress: account.emailAddress,
        workLocations: renderWorkLocations({
          isUserEnabled: account.enabled,
          inviteStatus: account.customerUserInviteStatus,
          workLocationIds: account.customerUserWorkLocationIds,
        }),
        customerUserInviteStatus: renderInviteStatus({
          isUserEnabled: account.enabled,
          inviteStatus: account.customerUserInviteStatus,
        }),
        more: canEditCustomer && (
          <ActionsIconButton
            size="small"
            onClick={(e) => onMenuOpen(e.currentTarget, id)}
          >
            <MoreIcon />
          </ActionsIconButton>
        ),
      },
      disabled:
        !account.enabled &&
        account.customerUserInviteStatus !== CustomerUserInviteStatus.UNINVITED,
      id,
    }));

  const headers = [
    { text: t("common:name") },
    { text: t("customers:usersNS:title") },
    { text: t("common:phone") },
    { text: t("common:email") },
    { text: t("customers:workLocations") },
    { text: t("common:status") },
    { text: "more" },
  ];

  const columnWidths = ["20%"];
  const addButtonLabel = t("common:user");

  // action menu open/close functions
  const onMenuOpen = (anchorEl: HTMLButtonElement, userId: string) => {
    setMenuState({ anchorEl, userId });
  };
  // clear user id after menu has closed to avoid flickering the conditional menu items
  const onMenuExited = () => setMenuState({ ...menuState, userId: "" });
  const onMenuClose = () => setMenuState({ ...menuState, anchorEl: null });

  // dialog open/close functions
  const onOpen = (id?: string) => setModalState({ show: true, id });

  const validateEmailAddress: Validate<string, FieldValues> = (
    emailAddress: string,
  ) => {
    const isDirty = !!methods.formState.dirtyFields["emailAddress"];

    if (!isDirty) return true;

    const existingUser = allCustomerUsers.find(
      (customerUser) => customerUser.account.emailAddress === emailAddress,
    );

    if (existingUser) {
      const emailInUse = t("customers:usersNS:emailInUse");

      // customer contact person or some error has occurred during updating workLocations -> user not showing in the table
      const emptyWorkLocations = isEmpty(
        existingUser.account.customerUserWorkLocationIds,
      );

      return emptyWorkLocations || emailInUse;
    }

    return true;
  };

  const dispatchInviteExistingCustomerUser = async (
    existingCustomerUser: InviteExistingCustomerUser,
  ) => {
    return await dispatch(
      inviteExistingCustomerAppUser(existingCustomerUser),
    ).then((action) => {
      if (isFulfilled(action)) {
        return action;
      }
    });
  };

  const dispatchCreateCustomerUser = async (params: CreateCustomerUser) => {
    return await dispatch(createNewCustomerUser(params)).then((action) => {
      if (isFulfilled(action)) {
        return action;
      }
    });
  };

  const dispatchUpdateCustomerUser = async (params: UpdateCustomerUser) =>
    await dispatch(updateCustomerAppUser(params)).then((action) => action);

  const dispatchUpdateCustomerUserEnabledState = async (
    customerUserEnabledState: UpdateCustomerUserEnabledState,
  ) => {
    return await dispatch(
      updateCustomerAppUserEnabledState(customerUserEnabledState),
    ).then((action) => {
      if (isFulfilled(action)) {
        const enabledState = action.meta.arg.enabled;
        const message = enabledState
          ? t("customers:usersNS:userActivated")
          : t("customers:usersNS:userDeactivated");
        sendMessage(message, "success");
        return action;
      }
    });
  };

  const dispatchSetTempPassword = () => {
    return dispatch(setCustomerUserTemporaryPassword(selectedUser!!.id));
  };

  const onConfirm = async (data: FieldValues) => {
    const existingCustomerUser = customerUsersByCompanyId.find(
      (customerUser) =>
        customerUser.id === data.id ||
        customerUser.account.emailAddress === data.emailAddress,
    );

    const customerUserWorkLocations: AutocompleteType[] = methods.getValues(
      "customerUserWorkLocationIds",
    );

    const autocompleteType =
      methods.formState.defaultValues?.customerUserWorkLocationIds || [];
    const defaultCustomerUserWorkLocations =
      autocompleteType as unknown as AutocompleteType[];

    const { addTo, removeFrom } = compareWorkLocations(
      customerUserWorkLocations,
      defaultCustomerUserWorkLocations,
    );

    if (existingCustomerUser) {
      const workLocationsChanged =
        !!methods.formState.dirtyFields["customerUserWorkLocationIds"];

      // update existing customer user
      const customerUserChanged =
        !!methods.formState.dirtyFields["emailAddress"] ||
        !!methods.formState.dirtyFields["firstName"] ||
        !!methods.formState.dirtyFields["lastName"] ||
        !!methods.formState.dirtyFields["title"] ||
        !!methods.formState.dirtyFields["phone"];

      if (customerUserChanged) {
        const params = {
          customerUserId: existingCustomerUser.id,
          version: existingCustomerUser.account.version,
          emailAddress: data.emailAddress,
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          title: data.title,
        };

        dispatchUpdateCustomerUser(params).then((action) => {
          if (isFulfilled(action)) {
            if (!workLocationsChanged) {
              sendMessage(t("customers:usersNS:userUpdated"), "success");
              onClose(false);
            }
          }
        });
      }

      if (workLocationsChanged) {
        const updateWorkLocations = {
          customerUserId: existingCustomerUser.id,
          addTo,
          removeFrom,
        };

        dispatch(updateCustomerUserWorkLocations(updateWorkLocations)).then(
          (action) => {
            if (isFulfilled(action)) {
              dispatch(
                setWorkLocationsOrderingUsers({
                  customerUserId: updateWorkLocations.customerUserId,
                  addTo,
                  removeFrom,
                  events: action.payload,
                }),
              );
              sendMessage(t("customers:usersNS:userUpdated"), "success");
              onClose(false);
            }
          },
        );
      }
    } else {
      const createCustomerUser: CreateCustomerUser = {
        firstName: data.firstName,
        lastName: data.lastName,
        emailAddress: data.emailAddress,
        title: data.title,
        phone: data.phone,
        customerCompanyId: customerCompanyId as string,
      };

      dispatchCreateCustomerUser(createCustomerUser).then((action) => {
        if (isFulfilled(action)) {
          const { addTo, removeFrom } = compareWorkLocations(
            customerUserWorkLocations,
            defaultCustomerUserWorkLocations,
          );

          const customerUserCreated = action.payload.find(
            (event) => event.name === "custUsrCreated",
          );
          const workLocationsChanged =
            !!methods.formState.dirtyFields["customerUserWorkLocationIds"];

          if (customerUserCreated && workLocationsChanged) {
            const params = {
              customerUserId: customerUserCreated?.id,
              addTo,
              removeFrom,
            };

            // since after a successful invite/creation, the returned custUsrCreated event may not have been handled
            // by CoreUsersProjectionUpdater yet, we need to ensure by polling that the user exists
            // before we try to update their work locations
            dispatch(fetchCustomerUsersByIds([customerUserCreated.id])).then(
              (action) => {
                if (isFulfilled(action) && !isEmpty(action.payload.data)) {
                  dispatch(updateCustomerUserWorkLocations(params)).then(
                    (action) => {
                      if (isFulfilled(action)) {
                        dispatch(
                          setWorkLocationsOrderingUsers({
                            customerUserId: params.customerUserId,
                            addTo,
                            removeFrom,
                            events: action.payload,
                          }),
                        );
                        sendMessage(
                          t("customers:usersNS:successfulCreation"),
                          "success",
                        );
                        onClose(false);
                      } else {
                        // invite was successful, work location update failed
                        sendMessage(
                          t(
                            "customers:usersNS:successfulCreationvWrklUpdFailed",
                          ),
                        );
                        onClose(false);
                      }
                    },
                  );
                } else {
                  // CoreUsersProjectionUpdater has not yet handled the custUsrCreated event, start polling
                  setUserIdToPoll(customerUserCreated.id);
                  return startPolling();
                }
              },
            );
          }
        }
      });
    }
  };
  // clear id after dialog has closed to avoid flickering the title on the close animation
  const onExited = () => setModalState({ ...modalState, id: "" });

  const showEmptyState = !isLoading && !formattedCustomerUsers.length;

  const handleUpdateCustomerUserEnabledState = ({
    customerUserId,
    enabled,
  }: {
    customerUserId: string;
    enabled: boolean;
  }) => {
    const existingCustomerUser = customerUsersByCompanyId.find(
      (customerUser) => customerUser.id === customerUserId,
    );

    if (existingCustomerUser) {
      const userEnabledStateObject = {
        id: customerUserId,
        version: existingCustomerUser.account.version,
        enabled,
      };

      dispatchUpdateCustomerUserEnabledState(userEnabledStateObject);
    }
  };

  const menuActions = {
    [MenuItemKey.edit]: (id: string) => onOpen(id),
    [MenuItemKey.invite]: (id: string) => {
      dispatchInviteExistingCustomerUser({ id }).then((action) => {
        if (isFulfilled(action)) {
          sendMessage(t("customers:usersNS:successfulInvite"), "success");
        } else {
          sendMessage(t("customers:usersNS:inviteFailed"), "error");
        }
      });
    },
    [MenuItemKey.disable]: (id: string) => {
      // disable / deactivate customer user
      handleUpdateCustomerUserEnabledState({
        customerUserId: id,
        enabled: false,
      });
    },
    [MenuItemKey.enable]: (id: string) => {
      // enable / activate customer user
      handleUpdateCustomerUserEnabledState({
        customerUserId: id,
        enabled: true,
      });
    },
    [MenuItemKey.resendInvitation]: (id: string) => {
      dispatchInviteExistingCustomerUser({ id }).then((action) => {
        if (isFulfilled(action)) {
          sendMessage(t("customers:usersNS:successfulInvite"), "success");
        } else {
          sendMessage(t("customers:usersNS:inviteFailed"), "error");
        }
      });
    },
    [MenuItemKey.resetPassword]: (id: string) => {
      setSelectedUser(allCustomerUsers.find((user) => user.id === id));
    },
  };

  const isValid = methods.formState.isValid;

  const requiredFields = methods.watch([
    "firstName",
    "lastName",
    "customerUserWorkLocationIds",
    "emailAddress",
  ]);

  const confirmDisabled =
    !methods.formState.isDirty ||
    !isEmpty(methods.formState.errors) ||
    requiredFields.some((field) => !field) ||
    !isValid ||
    isLoading ||
    isPolling;

  return (
    <Box width="100%">
      <OverlaySpinner isLoading={isLoading} />
      {canEditCustomer && (
        <AddButton
          label={addButtonLabel}
          variant="contained"
          sx={{
            margin: "12px",
            height: "42px",
            borderRadius: "20px",
            padding: "0 22px",
            fontSize: "1rem",
          }}
          onClick={() => onOpen()}
        />
      )}
      {showEmptyState ? (
        <UsersEmptyState />
      ) : (
        <FormContainer sx={{ height: "fit-content" }}>
          <FormTable
            data={formattedCustomerUsers}
            headers={headers}
            handleOpenEditModal={(_, id) => onOpen(id)}
            translationKeys={["workTitle"]}
            columnWidths={columnWidths}
            stripedTable
            disableEditRow
          />
        </FormContainer>
      )}
      <BasicDialog
        open={modalState.show}
        onCancel={() => onClose(true)}
        onConfirm={methods.handleSubmit(onConfirm)}
        title={
          modalState.id
            ? t("customers:usersNS:editUser") || ""
            : t("customers:usersNS:addUser") || ""
        }
        PaperProps={{ sx: { minWidth: "640px" } }}
        TransitionProps={{ onExited }}
        confirmDisabled={confirmDisabled}
        noBorder
        content={
          <FormProvider {...methods}>
            <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
              <Grid container alignItems="center" columnSpacing={2} rowGap={1}>
                <UserForm
                  userId={modalState.id}
                  customerUsers={prefilledCustomerUsers}
                  validateEmailAddress={validateEmailAddress}
                  isCustomerUser
                  isPolling={isPolling}
                  workLocations={workLocations}
                />
              </Grid>
            </UnsavedChangesProvider>
          </FormProvider>
        }
      />
      {selectedUser && (
        <TempPasswordDialog
          isOpen={!!selectedUser}
          userName={`${selectedUser?.account.firstName} ${selectedUser?.account.lastName}`}
          userEmail={`${selectedUser?.account.emailAddress}`}
          onSetTempPassword={dispatchSetTempPassword}
          close={() => setSelectedUser(undefined)}
        />
      )}
      <UserActionsMenu
        open={!!menuState.anchorEl}
        anchorEl={menuState.anchorEl}
        onClose={onMenuClose}
        userId={menuState.userId}
        onExited={onMenuExited}
        menuActions={menuActions}
      />
    </Box>
  );
};

export default UsersTable;
